import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl } from '@core/tools';
import { IBindNewBankAccountParams, ITransactionRecord, ITransactionRecordParams } from '../../types/yibao-manage';

const hostUrl = getHostbaseUrl();

/**
 * 空中分账 - 易宝账户 - 分页查询商户绑定的银行账户
 */

export async function queryYibaoBankAccountList(params, companyTerminal = false) {
  let data = [];
  let total = 0;

  try {
    const { code, context, message } = await Http.post<{data: []; total: number}>(`${hostUrl}/pay/yop/${companyTerminal ? 'company' : 'agent'}/bank-account-list`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: []; total: number };

      data = items || [];
      total = Number(all) || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 空中分账 - 易宝账户 - 交易流水分页查询
 */

export async function queryTransactionRecord(params: ITransactionRecordParams, companyTerminal = false) {
  let data = [];
  let total = 0;

  try {
    const { code, context, message } = await Http.post<{data: ITransactionRecord[]; total: number}>(`${hostUrl}/pay/txn-record/${companyTerminal ? 'company' : 'agent'}`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: ITransactionRecord[]; total: number };

      data = items || [] as ITransactionRecord[];
      total = Number(all) || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 *  空中分账 - 易宝账户 - 易宝管理代理商提现记录
 */

export async function exportYibaoCashPage(params, companyTerminal = false) {
  let buffer = null as Blob | null;
  let fileName = ''; // 默认文件名

  try {
    const { code, context, message, headers } = await Http.post<Blob>(
      `${hostUrl}/pay/yop/merchant-withdraw/${companyTerminal ? 'company' : 'agent'}-withdraw-orders-export`, 
      params, 
      { responseType: 'blob' });

    if (headers['content-disposition']) {
      const match = headers['content-disposition'].match(/filename=(.+);?/);

      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].trim().replace(/"/g, ''));
      }
    }

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    buffer,
    fileName,
  };
}

/**
 * 空中分账 - 分账账户 - 易宝管理提现记录
 */
export async function queryYibaoCashPageList(params, companyTerminal = false) {
  let data = [];
  let total = 0;

  try {
    const { code, context, message } = await Http.post<{data: []; total: number}>(`${hostUrl}/pay/yop/merchant-withdraw/${companyTerminal ? 'company' : 'agent'}-withdraw-orders-page`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: []; total: number };

      data = items || [];
      total = Number(all) || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 空中分账 - 分账账户 - 易宝管理取消银行卡绑定
 */
export async function CancelBankAccount(params, companyTerminal = false) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/pay/yop/${companyTerminal ? 'company' : 'agent'}/cancel-bank-account`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 空中分账 - 分账账户 - 易宝管理银行卡绑定
 */
export async function bindNewBankAccount(params: IBindNewBankAccountParams, companyTerminal = false) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/pay/yop/${companyTerminal ? 'company' : 'agent'}/bind-new-bank-account`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 空中分账 - 易宝账户 - 交易流水统计
 */
export async function queryTxnRecordSummary(params: ITransactionRecordParams) {
  let inAmt: number | undefined = 0;
  let outAmt: number | undefined = 0;

  try {
    const { code, context, message } = await Http.post<{ inAmt: number; outAmt: number }>(
      `${hostUrl}/pay/txn-record/summary`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      inAmt = context?.inAmt || undefined;
      outAmt = context?.outAmt || undefined;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    inAmt,
    outAmt,
  };
}

/**
 * 空中分账 - 易宝账户 - 交易流水分页导出
 */
export async function exportTxnRecords(params?: ITransactionRecordParams, companyTerminal = false) {
  let buffer = null as Blob | null;
  let fileName = ''; // 默认文件名

  try {
    const { code, context, message, headers } = await Http.post<Blob>(
      `${hostUrl}/pay/txn-record/${companyTerminal ? 'company' : 'agent'}/export`,
      { ...params },
      { responseType: 'blob' },
    );

    if (headers['content-disposition']) {
      const match = headers['content-disposition'].match(/filename=(.+);?/);

      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].trim().replace(/"/g, ''));
      }
    }

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    buffer,
    fileName,
  };
}